import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { PublicClientApplication } from '@azure/msal-browser';
import { googleSsoClientId, microsoftSsoClientId, ssoNonceUrl } from '../../Config/config';

// Server nonces live 15 minutes; refresh well before that so an idle login page keeps working.
const NONCE_REFRESH_MS = 10 * 60 * 1000;
const NONCE_MAX_RETRIES = 3;
const NONCE_RETRY_DELAY_MS = 2000; // 2s, 4s, 6s between automatic retries
const BUTTON_MAX_WIDTH = 400; // Google renders its button at most 400px wide

let googleScriptPromise = null;
const loadGoogleScript = () => {
    if (window.google?.accounts?.id) {
        return Promise.resolve();
    }
    if (!googleScriptPromise) {
        googleScriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.onload = resolve;
            script.onerror = () => {
                googleScriptPromise = null;
                reject(new Error('Google Identity Services failed to load'));
            };
            document.head.appendChild(script);
        });
    }
    return googleScriptPromise;
};

let msalAppPromise = null;
const getMsalApp = () => {
    if (!msalAppPromise) {
        msalAppPromise = PublicClientApplication.createPublicClientApplication({
            auth: {
                clientId: microsoftSsoClientId,
                // "common" = work/school and personal Microsoft accounts
                authority: 'https://login.microsoftonline.com/common',
                redirectUri: `${window.location.origin}/sso-redirect.html`,
            },
            // Only the ID token is needed, once; keep nothing in browser storage.
            cache: { cacheLocation: 'memoryStorage' },
        }).catch((error) => {
            msalAppPromise = null;
            throw error;
        });
    }
    return msalAppPromise;
};

const MicrosoftLogo = () => (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
        <rect x="0" y="0" width="10" height="10" fill="#F25022" />
        <rect x="11" y="0" width="10" height="10" fill="#7FBA00" />
        <rect x="0" y="11" width="10" height="10" fill="#00A4EF" />
        <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
    </svg>
);

/**
 * "Sign in with Google" / "Sign in with Microsoft" buttons for the login page.
 * Gets a server-issued nonce, lets the provider embed it in a signed ID token, and hands the token to
 * `onCredential(provider, idToken)`. All verification happens on the backend.
 * @param disabled - blocks both buttons (e.g. while a login request is running)
 * @param canStart - optional check run before a sign-in starts; return false to stop it
 * @param onCredential - called with ("google" | "microsoft", idToken)
 * @param onError - called with a user-facing message
 */
const SsoLogin = ({ disabled, canStart, onCredential, onError }) => {
    const nonceRef = useRef(null);
    const [nonceVersion, setNonceVersion] = useState(0);
    const [unavailable, setUnavailable] = useState(false);
    const [googleReady, setGoogleReady] = useState(false);
    const [msalApp, setMsalApp] = useState(null);
    const [microsoftBusy, setMicrosoftBusy] = useState(false);
    const googleWrapperRef = useRef(null);
    const googleButtonRef = useRef(null);

    // Keep the latest callbacks without re-initialising the providers on every render.
    const callbacksRef = useRef({ canStart, onCredential, onError });
    callbacksRef.current = { canStart, onCredential, onError };

    const retryTimerRef = useRef(null);
    const retryCountRef = useRef(0);

    const refreshNonce = useCallback(async () => {
        clearTimeout(retryTimerRef.current);
        nonceRef.current = null;
        try {
            const response = await axios.post(ssoNonceUrl);
            const nonce = response.data?.data?.nonce;
            if (!nonce) {
                throw new Error('Missing nonce');
            }
            nonceRef.current = nonce;
            retryCountRef.current = 0;
            setUnavailable(false);
        } catch (error) {
            // Transient failures (network blip, rate limit) usually clear within seconds: retry quietly
            // a few times before telling the user.
            if (retryCountRef.current < NONCE_MAX_RETRIES) {
                retryCountRef.current += 1;
                retryTimerRef.current = setTimeout(refreshNonce, NONCE_RETRY_DELAY_MS * retryCountRef.current);
            } else {
                setUnavailable(true);
            }
        }
        setNonceVersion((version) => version + 1);
    }, []);

    const retryNonceNow = () => {
        retryCountRef.current = 0;
        setUnavailable(false);
        refreshNonce();
    };

    const submitCredential = useCallback((provider, idToken) => {
        // The nonce inside this token is single use; get a fresh one for any next attempt.
        refreshNonce();
        callbacksRef.current.onCredential(provider, idToken);
    }, [refreshNonce]);

    useEffect(() => {
        if (!googleSsoClientId && !microsoftSsoClientId) {
            return undefined;
        }
        refreshNonce();
        const timer = setInterval(refreshNonce, NONCE_REFRESH_MS);
        return () => {
            clearInterval(timer);
            clearTimeout(retryTimerRef.current);
        };
    }, [refreshNonce]);

    // Google: (re)initialise with the current nonce and render the official button.
    useEffect(() => {
        if (!googleSsoClientId || !nonceRef.current) {
            return undefined;
        }
        let cancelled = false;
        loadGoogleScript()
            .then(() => {
                if (cancelled || !googleButtonRef.current) {
                    return;
                }
                window.google.accounts.id.initialize({
                    client_id: googleSsoClientId,
                    nonce: nonceRef.current,
                    ux_mode: 'popup',
                    auto_select: false,
                    callback: (response) => {
                        if (response?.credential) {
                            if (callbacksRef.current.canStart && !callbacksRef.current.canStart()) {
                                return;
                            }
                            submitCredential('google', response.credential);
                        }
                    },
                });
                const width = Math.min(BUTTON_MAX_WIDTH, Math.max(200, googleWrapperRef.current?.offsetWidth || 320));
                googleButtonRef.current.innerHTML = '';
                window.google.accounts.id.renderButton(googleButtonRef.current, {
                    type: 'standard',
                    theme: 'outline',
                    size: 'large',
                    text: 'signin_with',
                    shape: 'rectangular',
                    logo_alignment: 'center',
                    width,
                });
                setGoogleReady(true);
            })
            .catch(() => {
                if (!cancelled) {
                    callbacksRef.current.onError('Google sign-in could not be loaded. Please check your connection or sign in with your email and password.');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [nonceVersion, submitCredential]);

    // Microsoft: create the MSAL client up front so the popup opens directly from the click.
    useEffect(() => {
        if (!microsoftSsoClientId) {
            return undefined;
        }
        let cancelled = false;
        getMsalApp()
            .then((app) => {
                if (!cancelled) {
                    setMsalApp(app);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    callbacksRef.current.onError('Microsoft sign-in could not be loaded. Please sign in with your email and password.');
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const signInWithMicrosoft = async () => {
        if (disabled || !msalApp || microsoftBusy) {
            return;
        }
        if (callbacksRef.current.canStart && !callbacksRef.current.canStart()) {
            return;
        }
        const nonce = nonceRef.current;
        if (!nonce) {
            callbacksRef.current.onError('Sign-in is not ready yet. Please try again in a moment.');
            return;
        }
        setMicrosoftBusy(true);
        try {
            const result = await msalApp.loginPopup({
                scopes: ['openid', 'profile', 'email'],
                prompt: 'select_account',
                nonce,
            });
            await msalApp.clearCache().catch(() => {});
            if (result?.idToken) {
                submitCredential('microsoft', result.idToken);
            }
        } catch (error) {
            const code = error?.errorCode;
            if (code === 'user_cancelled') {
                // Popup closed by the user: nothing to report.
            } else if (code === 'popup_window_error' || code === 'empty_window_error') {
                callbacksRef.current.onError('Please allow pop-ups for this site to sign in with Microsoft.');
            } else {
                callbacksRef.current.onError('Microsoft sign-in failed. Please try again.');
            }
        } finally {
            setMicrosoftBusy(false);
        }
    };

    if (!googleSsoClientId && !microsoftSsoClientId) {
        return null;
    }

    return (
        <div className="mt-3">
            <div className="flex items-center">
                <div className="flex-grow border-t border-[#E8DCC2]" />
                <span className="mx-3 font-sans_book_body text-[12px] text-[#969696] tracking-[-0.24px]">or</span>
                <div className="flex-grow border-t border-[#E8DCC2]" />
            </div>

            <div className="mx-auto mt-3 w-full space-y-2" style={{ maxWidth: BUTTON_MAX_WIDTH }}>
                {googleSsoClientId && (
                    <div ref={googleWrapperRef} className={`relative flex h-[40px] w-full justify-center ${disabled ? 'opacity-60' : ''}`}>
                        {!googleReady && !unavailable && (
                            <div className="absolute inset-0 animate-pulse rounded-[4px] border border-[#dadce0] bg-white" />
                        )}
                        <div ref={googleButtonRef} className="flex w-full justify-center" />
                        {disabled && <div className="absolute inset-0 cursor-not-allowed" />}
                    </div>
                )}

                {microsoftSsoClientId && (
                    <button
                        type="button"
                        onClick={signInWithMicrosoft}
                        disabled={disabled || !msalApp || microsoftBusy}
                        className="flex h-[40px] w-full items-center justify-center gap-3 rounded-[4px] border border-[#dadce0] bg-white px-3 text-[14px] font-medium tracking-[0.25px] text-[#3c4043] transition-colors duration-150 hover:border-[#d2e3fc] hover:bg-[#f8faff] focus:outline-none focus:ring-2 focus:ring-[#BC8B20] focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        <MicrosoftLogo />
                        <span>{microsoftBusy ? 'Signing in...' : 'Sign in with Microsoft'}</span>
                    </button>
                )}
            </div>

            {unavailable && (
                <p className="mt-2 text-[12px] text-[red]">
                    Single sign-on is temporarily unavailable. Please sign in with your email and password, or{' '}
                    <button type="button" onClick={retryNonceNow} className="underline hover:text-[#BC8B20]">try again</button>.
                </p>
            )}
        </div>
    );
};

export default SsoLogin;
